/**
 * Date & Time Formatting Utility
 * Formats date-time to DD-MM-YYYY HH:mm format
 */

/**
 * Formats date to DD-MM-YYYY HH:mm format
 * @param {string|Date} dateInput - Date string, ISO string, or Date object
 * @returns {string} Formatted date string or "--" if invalid
 */
export const formatTradeDateTime = (dateInput) => {
  try {
    if (!dateInput) {
      return '--';
    }

    console.log('📅 formatTradeDateTime input:', dateInput);

    // If input is a string in format "YYYY-MM-DD HH:mm:ss" or similar
    if (typeof dateInput === 'string') {
      // Check if it contains date and time parts
      const parts = dateInput.trim().split(/[\sT]/); // Split by space or 'T'
      
      console.log('📅 Split parts:', parts);
      
      if (parts.length >= 2) {
        const datePart = parts[0]; // "2025-12-17" or "17.12.2025"
        const timePart = parts[1].split('.')[0]; // "09:09:12" (remove milliseconds if any)
        
        console.log('📅 Date part:', datePart, 'Time part:', timePart);
        
        // Parse date part
        let year, month, day;
        if (datePart.includes('-')) {
          // Format: YYYY-MM-DD
          [year, month, day] = datePart.split('-');
        } else if (datePart.includes('.')) {
          // Format: DD.MM.YYYY
          [day, month, year] = datePart.split('.');
        } else {
          // Fallback to Date parsing
          const date = new Date(dateInput);
          if (isNaN(date.getTime())) return '--';
          
          year = date.getFullYear();
          month = (date.getMonth() + 1).toString().padStart(2, '0');
          day = date.getDate().toString().padStart(2, '0');
          const hours = date.getHours().toString().padStart(2, '0');
          const minutes = date.getMinutes().toString().padStart(2, '0');
          const seconds = date.getSeconds().toString().padStart(2, '0');
          
          return `${year}.${month}.${day} ${hours}:${minutes}:${seconds}`;
        }
        
        // Parse time part
        const [hours, minutes, seconds] = timePart.split(':');
        
        // Format with leading zeros
        const formattedYear = year.padStart(4, '0');
        const formattedMonth = month.padStart(2, '0');
        const formattedDay = day.padStart(2, '0');
        const formattedHours = (hours || '00').padStart(2, '0');
        const formattedMinutes = (minutes || '00').padStart(2, '0');
        const formattedSeconds = (seconds || '00').padStart(2, '0');
        
        const result = `${formattedYear}.${formattedMonth}.${formattedDay} ${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
        console.log('📅 Formatted result:', result);
        
        // Return in YYYY.MM.DD HH:mm:ss format (exact time from database)
        return result;
      }
    }

    // Fallback: Parse as Date object
    console.log('📅 Using fallback Date parsing');
    let date;
    if (dateInput instanceof Date) {
      date = dateInput;
    } else {
      date = new Date(dateInput);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return '--';
    }

    // Extract date components
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');

    // Format as YYYY.MM.DD HH:mm:ss
    return `${year}.${month}.${day} ${hours}:${minutes}:${seconds}`;

  } catch (error) {
    console.error('Error formatting date:', error, dateInput);
    return '--';
  }
};

/**
 * Alternative formatter for legacy date strings (DD.MM.YYYY format)
 * @param {string} dateString - Date string in DD.MM.YYYY format
 * @returns {string} Formatted date string or "--" if invalid
 */
export const formatLegacyDateTime = (dateString) => {
  try {
    if (!dateString || typeof dateString !== 'string') {
      return '--';
    }

    // Handle formats like "13.12.2025 19:37:45" or "13.12.2025"
    const parts = dateString.split(' ');
    const datePart = parts[0]; // "13.12.2025"
    const timePart = parts[1] || '00:00:00'; // "19:37:45" or default

    // Split date part
    const [day, month, year] = datePart.split('.');
    
    // Split time part to get hours, minutes, and seconds
    const [hours, minutes, seconds] = timePart.split(':');

    // Validate components
    if (!day || !month || !year || !hours || !minutes) {
      return '--';
    }

    // Format with leading zeros
    const formattedDay = day.padStart(2, '0');
    const formattedMonth = month.padStart(2, '0');
    const formattedHours = hours.padStart(2, '0');
    const formattedMinutes = minutes.padStart(2, '0');
    const formattedSeconds = (seconds || '00').padStart(2, '0');

    return `${year}.${formattedMonth}.${formattedDay} ${formattedHours}:${formattedMinutes}:${formattedSeconds}`;

  } catch (error) {
    console.error('Error formatting legacy date:', error, dateString);
    return '--';
  }
};